import React from "react";
import { Redirect, Route, useLocation } from "react-router-dom";

import { InputNumberProps } from "antd/lib/input-number";

import { IPricingPlanErrorPageProps } from "@templates/ZPricingPlanErrorPage/interface";
import {
	cache,
	CBSupportedCurrency,
	getDefaultGrowPrivateRoute,
	ZomentumRoutes,
	ZomentumStorageKeys
} from "@/Contracts";
import {
	DocumentApproval,
	DocumentApproverType
} from "@/Contracts/DocumentApprovals";
import { Document, QuoteTotal } from "@/Contracts/Documents";
import { ApprovalStatus } from "@/Contracts/Documents/Revision";
import { AuthToken } from "@/Contracts/Login";
import {
	ActivityMessage,
	ActivityMessageType
} from "@/Contracts/Others/RecentActivityFeed";
import {
	ProductSearchRequest,
	ProductSearchResponse,
	ProductSource,
	ProductType
} from "@/Contracts/Products";
import {
	DistributorRegions,
	EtilizeLocale,
	IngramRegions,
	SimilarEtilzieRegions
} from "@/Contracts/Settings";
import {
	AdminRoleOnly,
	ManagerAndAdminRoleOnly,
	PricingPlan,
	PricingPlanNames,
	TaxCategory,
	UserCompany,
	UserData,
	UserRole
} from "@/Contracts/Users";
import { emailRegex, getQueryParamAuthDetails } from "@/Contracts/Utils";
import { isEmbedded } from "@/Utils/superops/embedDetection";
import { isAllowedEmbedRoute } from "@/superops/utils/EmbedDetection";
import { DraggableLocation } from "react-beautiful-dnd";
import { useUserReady } from "./useUserReady";
import { buildLoginRedirectTarget } from "./buildLoginRedirectTarget";

import ZTooltip from "@atoms/ZTooltip";
import {
	AddQuoteProductCatalogDrawerFormValues,
	EditQuoteProductCatalogDrawerFormValues
} from "@/Contracts/ProposalBuilder/Block";
import { shortenText } from "@/Utils";
import { useSelector } from "react-redux";
import { setupDone, setupPending } from "@/V2Utils";
import { GlobalState } from "@/Reducers/interface";
import { cacheConstant } from "@/Contracts/appCache";
import { useFeatureFlag } from "@/Hooks/useFeature";

export const isRouteAllowed = (
	route: ZomentumRoutes,
	role?: UserRole | null
): boolean => {
	switch (route) {
		case ZomentumRoutes.PipelineSettings:
		case ZomentumRoutes.CustomFieldsList:
		case ZomentumRoutes.CustomFieldsSettings:
		case ZomentumRoutes.TimezoneSettings:
		case ZomentumRoutes.InvoicesList:
		case ZomentumRoutes.CancelSubscription:
		case ZomentumRoutes.TaxRegionSettings:
		case ZomentumRoutes.AutoTaskIntegration:
		case ZomentumRoutes.ConnectWiseIntegration:
		case ZomentumRoutes.SyncroIntegration:
		case ZomentumRoutes.DocumentApproval:
		case ZomentumRoutes.CustomDistributorIntegrationAdd:
		case ZomentumRoutes.CustomDistributorIntegrationDetail: {
			if (role && AdminRoleOnly.indexOf(role) > -1) {
				return true;
			}
			return false;
		}
		case ZomentumRoutes.ReportsList:
		case ZomentumRoutes.ReportsDetail:
		case ZomentumRoutes.CreateReport:
		case ZomentumRoutes.SalesActivityAutomationCreate:
		case ZomentumRoutes.SalesActivityAutomationsEdit:
		case ZomentumRoutes.SalesActivityAutomationsClone:
		case ZomentumRoutes.SalesActivityAutomationsList: {
			if (role && ManagerAndAdminRoleOnly.indexOf(role) > -1) {
				return true;
			}
			return false;
		}
		case ZomentumRoutes.Settings:
		default: {
			return true;
		}
	}
};

export const isRouteAllowedForCurrentPlan = (
	route: ZomentumRoutes,
	subscriptions: PricingPlan[] | null
): IPricingPlanErrorPageProps | null => {
	const currentPlan = subscriptions?.find(plan => plan.current_plan);

	switch (route) {
		case ZomentumRoutes.DocumentApproval:
		case ZomentumRoutes.DocumentApprovalCreatePage:
		case ZomentumRoutes.DocumentApprovalDetailsPage:
			if (
				currentPlan &&
				!(
					currentPlan.name.includes(PricingPlanNames.GROWTH) ||
					currentPlan.name.includes(PricingPlanNames.PRO) ||
					currentPlan.name.includes(PricingPlanNames.ENTERPRISE)
				)
			) {
				return {
					title: "Approval Rules: Stay in Control, Empower Your Team",
					description:
						"Define clear checkpoints, prevent errors, and maintain control across all documents."
				};
			}
			return null;

		case ZomentumRoutes.CustomDistributorIntegrationAdd:
		case ZomentumRoutes.CustomDistributorIntegrationDetail:
			if (
				currentPlan &&
				currentPlan.name.includes(PricingPlanNames.IGNITE)
			) {
				return {
					title: "You’ve unlocked premium feature",
					description:
						"Build your own custom distributor integration with zomentum"
				};
			}
			return null;
		default:
			return null;
	}
};

export const PrivateRoute = React.memo(
	({ component: PrivateComponent, ...routeProps }: any) => {
		// All hooks must be called first (React Hooks rules)
		const [userReady] = useUserReady();
		const location = useLocation();
		const configurationSettings = useSelector(
			(state: GlobalState) => state.user.configurationSettings
		);

		// Non-hook logic
		const authToken = cache.get(
			ZomentumStorageKeys.Token,
			true
		) as AuthToken;
		const pathname = location["pathname"];
		const target = buildLoginRedirectTarget(location);

		// Check embed route restrictions
		if (isEmbedded() && !isAllowedEmbedRoute(pathname)) {
			const redirectUrl = `/error${location.search}`;
			return <Redirect to={redirectUrl} />;
		}

		const userRoleAllowedToAccessRoute = isRouteAllowed(
			routeProps.path as ZomentumRoutes,
			userReady?.userData?.role
		);

		/** isRouteAllowedForCurrentPlan - will return null if the route is allowed else it will retun the warning message.
		 * warning message is an object with "title" and "description"
		 */
		const currentPricingPlanAllowedToAccessRoute =
			isRouteAllowedForCurrentPlan(
				routeProps.path as ZomentumRoutes,
				userReady?.subscriptions ?? null
			);
		if (
			setupPending({
				...configurationSettings,
				company: configurationSettings?.company_configs
			}) &&
			routeProps.path !== ZomentumRoutes.SetupAccount
		) {
			return <Redirect to={ZomentumRoutes.SetupAccount} />;
		}

		if (
			setupDone(configurationSettings) &&
			routeProps.path === ZomentumRoutes.SetupAccount
		) {
			return <Redirect to={ZomentumRoutes.OpportunityList} />;
		}

		if (!authToken?.token.length) {
			return (
				<Redirect
					to={{
						pathname: ZomentumRoutes.Login,
						state: { target }
					}}
				/>
			);
		}

		if (!routeProps?.isSetupUserReady && !userReady?.isUserReady) {
			return null;
		}

		const isDocumentApprovalRoute = [
			ZomentumRoutes.DocumentApproval,
			ZomentumRoutes.DocumentApprovalDetailsPage,
			ZomentumRoutes.DocumentApprovalCreatePage
		].includes(routeProps.path as ZomentumRoutes);

		return (
			<Route
				{...routeProps}
				render={props =>
					!!currentPricingPlanAllowedToAccessRoute ? (
						<Redirect
							to={{
								pathname: isDocumentApprovalRoute
									? ZomentumRoutes.DocumentApprovalPricingPlanErrorPage
									: ZomentumRoutes.PricingPlanErrorPage,
								state: {
									title: currentPricingPlanAllowedToAccessRoute.title,
									description:
										currentPricingPlanAllowedToAccessRoute.description
								}
							}}
						/>
					) : (
						<>
							{userRoleAllowedToAccessRoute ? (
								<PrivateComponent {...props} />
							) : (
								<Redirect to={ZomentumRoutes.Unauthorized} />
							)}
						</>
					)
				}
			/>
		);
	}
);

export const PrivateSetupRoute = React.memo(
	({ component: PrivateComponent, ...routeProps }: any) => {
		return (
			<PrivateRoute
				component={PrivateComponent}
				isSetupUserReady={true}
				{...routeProps}
			/>
		);
	}
);

export const PublicRoute = ({
	component: PublicComponent,
	...routeProps
}: any) => {
	const routeLinkProps = { ...routeProps };
	let componentProps = {};
	if (
		Object.prototype.hasOwnProperty.call(routeLinkProps, "componentProps")
	) {
		componentProps = routeLinkProps.componentProps;
		delete routeLinkProps.componentProps;
	}
	if (PublicComponent) {
		return (
			<Route
				{...routeLinkProps}
				render={props => (
					<PublicComponent {...props} {...componentProps} />
				)}
			/>
		);
	}
	return <Route {...routeLinkProps} />;
};

export const LoginRoute = ({
	component: LoginComponent,
	...routeProps
}: any) => {
	const showPartnerAlignFeaturesFlag = useFeatureFlag(
		"show_partner_align_features"
	);
	const routeLinkProps = { ...routeProps };
	let componentProps = {};
	if (
		Object.prototype.hasOwnProperty.call(routeLinkProps, "componentProps")
	) {
		componentProps = routeLinkProps.componentProps;
		delete routeLinkProps.componentProps;
	}
	const authToken = cache.get(ZomentumStorageKeys.Token, true) as AuthToken;

	const routeToRedirect = getDefaultGrowPrivateRoute(
		showPartnerAlignFeaturesFlag
	);
	return (
		<Route
			{...routeLinkProps}
			render={props =>
				!authToken || !authToken.token ? (
					<LoginComponent {...props} {...componentProps} />
				) : (
					<Redirect to={routeToRedirect} />
				)
			}
		/>
	);
};

export const checkValidImageURL = (url: string): boolean => {
	const extRegex = /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i;
	return !!url.match(extRegex);
};

export const getNarrowedCurrencySymbol = (currency: string) => {
	return new Intl.NumberFormat("en-US", {
		// use any locale since we need narrowed symbol
		style: "currency",
		currencyDisplay: "narrowSymbol",
		currency: currency,
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	})
		.format(0)
		.replace(/\d/g, "")
		.trim();
};

export const oppValuesFormatter = (value = 0, decimals: number) => {
	if (!value) {
		return 0;
	}
	return parseFloat(Number(value).toFixed(decimals));
};

export const percentageInputProps: InputNumberProps = {
	formatter: (value: string | number | undefined): string =>
		Number(value ? value : 0).toString() + " %",
	parser: (value: string | undefined): number | string =>
		(value && Number(value) ? Number(value) : 0).toString(),
	min: 0,
	max: 100
};

export const reorder = (list: any[], startIndex: number, endIndex: number) => {
	const result = Array.from(list);
	const [removed] = result.splice(startIndex, 1);
	result.splice(endIndex, 0, removed);

	return result;
};

/**
 * Moves an item from one list to another list.
 */
export const move = (
	source: any[],
	destination: any[],
	droppableSource: DraggableLocation,
	droppableDestination: DraggableLocation
) => {
	const sourceClone = Array.from(source);
	const destClone = Array.from(destination);
	const [removed] = sourceClone.splice(droppableSource.index, 1);

	destClone.splice(droppableDestination.index, 0, removed);

	const result: Record<string, any[]> = {};
	result[droppableSource.droppableId] = sourceClone;
	result[droppableDestination.droppableId] = destClone;

	return result;
};

export const checkCanMultiplePricingPlansFor = (
	productType: ProductType | null
): boolean => {
	return (
		!!productType &&
		![ProductType.Bundle, ProductType.Hardware].includes(productType)
	);
};

/**
 *
 * @param url url to extract domain
 */
export const extractHostname = (url: string) => {
	let hostname = new URL(url).hostname;
	// find & remove www
	hostname =
		hostname.indexOf("www.") > -1 ? hostname.split("www.")[1] : hostname;
	// find & remove port number
	hostname = hostname.split(":")[0];
	// find & remove "?"
	hostname = hostname.split("?")[0];
	return hostname;
};

export const getErrorMessageForIncompleteForm = (json: any): string => {
	const result: Record<string, string> = {};

	Object.keys(json).forEach(key => {
		typeof json[key] === "object"
			? (result[key] = getErrorMessageForIncompleteForm(json[key]))
			: (result[key] = json[key]);
	});

	return Object.values(result)[0];
};

export const preventDefault = (
	event: React.MouseEvent<HTMLDivElement, MouseEvent>
) => {
	return event.preventDefault();
};

export const formLayout = {
	labelCol: { span: 5 },
	wrapperCol: { span: 19 }
};

export const getApprovalRuleFitlers = (
	documentApproval: DocumentApproval
): string[] => {
	const filters: string[] =
		documentApproval.filter_criteria
			?.map(filter => {
				if (
					!!filter?.filter_operator?.id_value?.length &&
					!!filter?.filter_operator?.display_value?.length
				) {
					return `${filter.field_display_name} ${
						filter.filter_operator.display_value
					} ${
						filter.values.length > 1
							? filter.values
									?.slice(0, -1)
									.map(v => v.display_value)
									.join(", ") +
							  " and " +
							  filter.values?.slice(-1)[0].display_value
							: filter.values.map(v => v.display_value).join("")
					}`;
				}
				return [];
			})
			?.flat() ?? [];

	return filters;
};

export const getAllApprovers = (
	documentApprovalRules: DocumentApproval[],
	companyUserList: UserData[]
): string[] => {
	const allAdmins: string[] = companyUserList
		.filter(
			currentUser =>
				currentUser?.role && AdminRoleOnly?.includes(currentUser?.role)
		)
		.map(user => user.id);

	const allApprovers: string[] = documentApprovalRules.reduce(
		(ids: string[], current: DocumentApproval) => {
			if (
				current.approver_automation_details.document_approver_type ===
				DocumentApproverType.AllAdmins
			) {
				ids.push(...allAdmins);
			} else {
				if (current.approver_automation_details.approver_user_ids) {
					ids.push(
						...current.approver_automation_details.approver_user_ids
					);
				}
			}

			return ids;
		},
		[]
	);

	return allApprovers;
};

export const canCurrentUserApprove = (
	document: Document,
	userData: UserData,
	allApprovers: string[]
): boolean => {
	const isCurrentUserAdmin =
		userData?.role && AdminRoleOnly?.includes(userData?.role);

	const isCurrentAdminApprover =
		userData?.id &&
		document?.approval_required &&
		allApprovers?.includes(userData?.id) &&
		isCurrentUserAdmin;

	return !!isCurrentAdminApprover;
};

export const showApprovalWarningEndClient = (document?: Document | null) => {
	return (
		!!document &&
		!!getQueryParamAuthDetails() &&
		document?.approval_required &&
		!!document?.revisions?.[0] &&
		document?.revisions?.[0]?.approval_details.approval_status !==
			ApprovalStatus.Approved
	);
};

export const compareEtilizeRegion = (
	distributorRegion: string,
	etilizeRegion: EtilizeLocale,
	isIcecatIntegrationEnabled: boolean
): boolean => {
	distributorRegion = distributorRegion.toLowerCase();
	const icecatSimilarRegions = {
		ca: "us",
		nz: "au"
	};
	const replacedRegion =
		Object.entries(
			isIcecatIntegrationEnabled
				? icecatSimilarRegions
				: SimilarEtilzieRegions
		).find(region => region[0] === distributorRegion)?.[1] ??
		distributorRegion;
	return `en_${replacedRegion}` === etilizeRegion;
};

export const validateEtilizeContentSource = (
	distributorRegion: string | null | undefined,
	userCompany: UserCompany,
	isIcecatIntegrationEnabled: boolean
): boolean => {
	if (!!distributorRegion) {
		return compareEtilizeRegion(
			distributorRegion,
			(isIcecatIntegrationEnabled
				? userCompany.settings?.icecat_locale
				: userCompany.settings?.etilize_locale) ?? EtilizeLocale.US,
			isIcecatIntegrationEnabled
		);
	}
	return false;
};

export const isEtilizeVendorProductId = (vendor_product_id: string) => {
	return vendor_product_id.includes("etilize");
};

export const handleActivityLinkClick = (
	messageType: ActivityMessageType,
	value: string
) => {
	if (
		messageType === ActivityMessageType.REDIRECT &&
		value &&
		value.length > 0
	) {
		window.open(value);
	}
};

export const formActivityText = (
	activities: ActivityMessage[],
	shouldShortenText = true
) => {
	const textActivity: ActivityMessage = {
		text: "",
		message_type: ActivityMessageType.TEXT,
		value: null
	};

	const activityMessageArray = activities.reduce(
		(
			final: ActivityMessage[],
			activity: ActivityMessage,
			index: number
		) => {
			if (activity.message_type === ActivityMessageType.TEXT) {
				textActivity.text = textActivity.text + " " + activity.text;
			} else {
				final.push({ ...textActivity });
				textActivity.text = "";
				final.push(activity);
			}

			if (index === activities.length - 1 && !!textActivity.text) {
				final.push(textActivity);
			}

			return final;
		},
		[]
	);

	return activityMessageArray.map(({ message_type, text, value }, index) =>
		message_type === ActivityMessageType.TEXT || !value ? (
			<span key={index} className="mr-1">
				{shouldShortenText ? shortenText(text, 45) : text}
			</span>
		) : (
			value && (
				<ZTooltip title={text} key={index}>
					<span
						className="link"
						onClick={() =>
							handleActivityLinkClick(message_type, value)
						}
						key={index}
					>
						{shouldShortenText ? shortenText(text, 45) : text}
					</span>
				</ZTooltip>
			)
		)
	);
};

export const isEtilizeFiltersDropped = (
	productSearchRequest: ProductSearchRequest,
	productsSearchResponse: ProductSearchResponse
) => {
	if (
		productSearchRequest.filter_by_item_sources.includes(
			ProductSource.Etilize
		)
	) {
		const requestedFiltersCount =
			productSearchRequest.filter_by_categories.length +
			productSearchRequest.filter_by_manufacturers.length;
		const recievedFiltersCount =
			productsSearchResponse.meta_info.categories.length +
			productsSearchResponse.meta_info.manufacturers.length;
		return recievedFiltersCount < requestedFiltersCount;
	}
	return false;
};

export const isPayableCurrency = (currency: string): boolean => {
	const cbSupportCurrencies = Object.values(CBSupportedCurrency).map(
		supportedCurrency => supportedCurrency as string
	);

	return cbSupportCurrencies.some(
		supportedCurrency => supportedCurrency === currency
	);
};

export const validateEmails = (emails: string[]) => {
	return emails.every(
		email =>
			email.match(emailRegex) || email === "owner" || email === "contacts"
	);
};

export const isStringAnInteger = (str: string) => {
	if (!str) {
		return false;
	}

	// RegEx replacement for escaping Zero(s) before the number
	str = str.replace(/^0+/, "") || "0";
	const n = Math.floor(Number(str));
	return n !== Infinity && String(n) === str && n >= 0;
};

export const sumTotal = (
	a: QuoteTotal | null,
	b: QuoteTotal | null
): QuoteTotal | null => {
	if (!a || !b) return b || a;
	return {
		one_time: a.one_time + b.one_time,
		quarterly: a.quarterly + b.quarterly,
		monthly: a.monthly + b.monthly,
		weekly: a.weekly + b.weekly,
		annual: a.annual + b.annual,
		grand: a.grand + b.grand,
		semi_annually: a.semi_annually + b.semi_annually
	};
};

export const handleRegionTextIngram = (value: string): string => {
	switch (value) {
		case DistributorRegions.USA:
			return `${IngramRegions.us}`;
		case DistributorRegions.UK:
			return `${IngramRegions.uk}`;
		case DistributorRegions.Australia:
			return `${IngramRegions.au}`;
		case DistributorRegions.Canada:
			return `${IngramRegions.ca}`;
		case DistributorRegions.NZ:
			return `${IngramRegions.nz}`;
		case DistributorRegions.AT:
			return `${IngramRegions.at}`;
		case DistributorRegions.DE:
			return `${IngramRegions.de}`;
		case DistributorRegions.SE:
			return `${IngramRegions.se}`;
		case DistributorRegions.CH:
			return `${IngramRegions.ch}`;
		case DistributorRegions.HU:
			return `${IngramRegions.hu}`;
		case DistributorRegions.ES:
			return `${IngramRegions.es}`;
		case DistributorRegions.FR:
			return `${IngramRegions.fr}`;
		case DistributorRegions.IT:
			return `${IngramRegions.it}`;
		case DistributorRegions.PT:
			return `${IngramRegions.pt}`;
		case DistributorRegions.Netherlands:
			return `${IngramRegions.nl}`;
		default:
			return "";
	}
};
export const dropdownPlacements = {
	bottomLeft: {
		points: ["tl", "bl"],
		offset: [0, 4],
		overflow: {
			adjustX: 0,
			adjustY: 1
		}
	},
	topLeft: {
		points: ["bl", "tl"],
		offset: [0, -4],
		overflow: {
			adjustX: 0,
			adjustY: 1
		}
	}
};

export const getDocumentDateTimePattern = (
	document: Document | null,
	datePattern: string
) => {
	return datePattern.trim().length
		? datePattern
		: document?.user?.user_settings?.date_pattern?.trim().length
		? document?.user?.user_settings?.date_pattern
		: document?.company?.settings?.date_pattern ?? "";
};

export const getInitialTaxCategoryId = (
	defaultData:
		| AddQuoteProductCatalogDrawerFormValues
		| EditQuoteProductCatalogDrawerFormValues
		| null,
	taxCategoriesList: TaxCategory[],
	currentActiveDocument: Document | null
): string | undefined => {
	if (currentActiveDocument?.tax_region_id) {
		if (!!defaultData?.tax_category_id?.length) {
			const defaultTaxCategory = taxCategoriesList.find(
				taxCategory => taxCategory.id === defaultData.tax_category_id
			);
			if (!defaultTaxCategory || defaultTaxCategory?.disabled) {
				return;
			}
			return defaultData?.tax_category_id;
		} else if (defaultData?.product_source === ProductSource.Etilize) {
			return taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.includes(
						ProductType.Hardware
					)
			)?.id;
		} else if (!!defaultData?.product_type?.length) {
			return taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.includes(
						defaultData?.product_type as ProductType
					)
			)?.id;
		}
	} else {
		if (defaultData?.product_source === ProductSource.Etilize) {
			return taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.includes(
						ProductType.Hardware
					)
			)?.id;
		} else if (!!defaultData?.product_type?.length) {
			return taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					(taxCategory.default_for_types?.includes(
						defaultData?.product_type as ProductType
					) ||
						taxCategory.id === defaultData?.tax_category_id)
			)?.id;
		}
	}
	return;
};

export const isDashboardAllowedForPartner = () => {
	const configurationSettings = cache.get(
		cacheConstant.configurationSettings,
		true
	);
	const isVendorAssociated =
		configurationSettings?.is_associated_with_vendors_other_than_default;

	return isVendorAssociated;
};
